/**
 * Gas Sensor Calculations & Utilities (v2.4.0)
 *
 * Features:
 * - Multi-gas detection (CH4, CO, LPG, H2S, NH3, CO2, etc.)
 * - Toxicity level classification
 * - Explosion risk assessment
 * - Leak detection and rate estimation
 * - Gas statistics and analytics
 * - Multi-language support (Arabic/English)
 * - RTL support
 *
 * @version 2.4.0
 * @date 2026-01-23
 */

export type GasType = 'CH4' | 'CO' | 'LPG' | 'H2S' | 'NH3' | 'CO2' | 'O2' | 'VOC' | 'unknown';
export type ToxicityLevel = 'safe' | 'low' | 'moderate' | 'high' | 'critical' | 'lethal';
export type ExplosionRisk = 'none' | 'low' | 'moderate' | 'high' | 'critical';
export type LeakSeverity = 'none' | 'minor' | 'moderate' | 'major' | 'severe';

export interface GasReading {
  gasType: GasType;
  concentration: number; // ppm (parts per million)
  timestamp: Date;
  temperature?: number;
  humidity?: number;
}

export interface GasThresholds {
  safe: number;
  warning: number;
  danger: number;
  critical: number;
  lel: number; // Lower Explosive Limit (%)
  uel: number; // Upper Explosive Limit (%)
}

export interface GasStats {
  avgConcentration: number;
  maxConcentration: number;
  minConcentration: number;
  peakTime: Date | null;
  safeHours: number;
  warningHours: number;
  dangerHours: number;
  criticalHours: number;
  leakDetectionCount: number;
  avgLeakDuration: number; // minutes
}

export interface LeakInfo {
  isLeaking: boolean;
  severity: LeakSeverity;
  duration: number; // minutes
  peakConcentration: number;
  estimatedRate: number; // ppm per minute
  estimatedSource: 'point' | 'diffuse' | 'unknown';
}

export interface ExplosionRiskInfo {
  riskLevel: ExplosionRisk;
  lelPercentage: number; // % of Lower Explosive Limit
  isExplosive: boolean;
  evacuationRequired: boolean;
  timeToLEL: number | null; // minutes (if trending towards LEL)
}

export interface GasStatus {
  gasType: GasType;
  currentConcentration: number;
  toxicityLevel: ToxicityLevel;
  explosionRisk: ExplosionRiskInfo;
  leakInfo: LeakInfo;
  todayStats: GasStats;
  yesterdayStats: GasStats;
  weekStats: GasStats;
  monthStats: GasStats;
  recommendations: string[];
  emergencyActions: string[];
}

// ==================== Gas Thresholds Database ====================

const GAS_THRESHOLDS: Record<GasType, GasThresholds> = {
  CH4: {
    // Methane (Natural Gas)
    safe: 1000,
    warning: 5000,
    danger: 10000,
    critical: 25000,
    lel: 5.0, // 5% = 50,000 ppm
    uel: 15.0,
  },
  CO: {
    // Carbon Monoxide
    safe: 9,
    warning: 35,
    danger: 200,
    critical: 800,
    lel: 12.5, // 12.5% = 125,000 ppm
    uel: 74.0,
  },
  LPG: {
    // Liquefied Petroleum Gas (Propane/Butane)
    safe: 1000,
    warning: 5000,
    danger: 10000,
    critical: 21000,
    lel: 2.1, // 2.1% = 21,000 ppm
    uel: 9.5,
  },
  H2S: {
    // Hydrogen Sulfide
    safe: 10,
    warning: 20,
    danger: 50,
    critical: 100,
    lel: 4.0, // 4% = 40,000 ppm
    uel: 44.0,
  },
  NH3: {
    // Ammonia
    safe: 25,
    warning: 50,
    danger: 150,
    critical: 300,
    lel: 15.0, // 15% = 150,000 ppm
    uel: 28.0,
  },
  CO2: {
    // Carbon Dioxide
    safe: 1000,
    warning: 5000,
    danger: 10000,
    critical: 40000,
    lel: 0, // Not flammable
    uel: 0,
  },
  O2: {
    // Oxygen (deficiency monitoring)
    safe: 19.5, // Below 19.5% is dangerous
    warning: 18.0,
    danger: 16.0,
    critical: 12.0,
    lel: 0,
    uel: 0,
  },
  VOC: {
    // Volatile Organic Compounds
    safe: 500,
    warning: 3000,
    danger: 10000,
    critical: 25000,
    lel: 1.0, // Varies by compound
    uel: 10.0,
  },
  unknown: {
    safe: 100,
    warning: 500,
    danger: 1000,
    critical: 5000,
    lel: 1.0,
    uel: 10.0,
  },
};

// ==================== Core Functions ====================

/**
 * Classify toxicity level based on gas concentration
 */
export function getToxicityLevel(gasType: GasType, concentration: number): ToxicityLevel {
  const thresholds = GAS_THRESHOLDS[gasType];

  if (concentration <= thresholds.safe) return 'safe';
  if (concentration <= thresholds.warning) return 'low';
  if (concentration <= thresholds.danger) return 'moderate';
  if (concentration <= thresholds.critical) return 'high';
  if (concentration <= thresholds.critical * 2) return 'critical';
  return 'lethal';
}

/**
 * Assess explosion risk based on LEL percentage
 */
export function getExplosionRisk(
  gasType: GasType,
  concentration: number,
  recentReadings: GasReading[],
): ExplosionRiskInfo {
  const thresholds = GAS_THRESHOLDS[gasType];
  const lelPpm = thresholds.lel * 10000; // Convert % to ppm
  const lelPercentage = (concentration / lelPpm) * 100;

  let riskLevel: ExplosionRisk;
  let isExplosive = false;
  let evacuationRequired = false;

  if (lelPercentage < 10) {
    riskLevel = 'none';
  } else if (lelPercentage < 25) {
    riskLevel = 'low';
  } else if (lelPercentage < 50) {
    riskLevel = 'moderate';
  } else if (lelPercentage < 75) {
    riskLevel = 'high';
    evacuationRequired = true;
  } else {
    riskLevel = 'critical';
    isExplosive = true;
    evacuationRequired = true;
  }

  // Estimate time to LEL if trending upwards
  let timeToLEL: number | null = null;
  if (recentReadings.length >= 3 && lelPercentage < 100) {
    const sortedReadings = [...recentReadings]
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .slice(-10);

    if (sortedReadings.length >= 3) {
      const firstConc = sortedReadings[0].concentration;
      const lastConc = sortedReadings[sortedReadings.length - 1].concentration;
      const timeDiff =
        (new Date(sortedReadings[sortedReadings.length - 1].timestamp).getTime() -
          new Date(sortedReadings[0].timestamp).getTime()) /
        (1000 * 60); // minutes

      if (lastConc > firstConc && timeDiff > 0) {
        const rate = (lastConc - firstConc) / timeDiff;
        const remainingPpm = lelPpm - concentration;
        if (rate > 0) {
          timeToLEL = Math.round(remainingPpm / rate);
        }
      }
    }
  }

  return {
    riskLevel,
    lelPercentage,
    isExplosive,
    evacuationRequired,
    timeToLEL,
  };
}

/**
 * Detect gas leak and assess severity
 */
export function detectLeak(
  readings: GasReading[],
  currentConcentration: number,
  gasType: GasType,
): LeakInfo {
  const thresholds = GAS_THRESHOLDS[gasType];

  if (readings.length < 2) {
    return {
      isLeaking: currentConcentration > thresholds.warning,
      severity:
        currentConcentration > thresholds.critical
          ? 'severe'
          : currentConcentration > thresholds.danger
          ? 'major'
          : currentConcentration > thresholds.warning
          ? 'moderate'
          : 'none',
      duration: 0,
      peakConcentration: currentConcentration,
      estimatedRate: 0,
      estimatedSource: 'unknown',
    };
  }

  // Find start of potential leak
  const sortedReadings = [...readings].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );

  let leakStartIndex = -1;
  for (let i = sortedReadings.length - 1; i >= 0; i--) {
    if (sortedReadings[i].concentration <= thresholds.safe) {
      leakStartIndex = i + 1;
      break;
    }
  }

  if (leakStartIndex === -1) leakStartIndex = 0;

  const leakReadings = sortedReadings.slice(leakStartIndex);
  const isLeaking = currentConcentration > thresholds.warning && leakReadings.length > 0;

  if (!isLeaking || leakReadings.length === 0) {
    return {
      isLeaking: false,
      severity: 'none',
      duration: 0,
      peakConcentration: currentConcentration,
      estimatedRate: 0,
      estimatedSource: 'unknown',
    };
  }

  // Calculate leak duration
  const firstLeakTime = new Date(leakReadings[0].timestamp);
  const lastLeakTime = new Date(leakReadings[leakReadings.length - 1].timestamp);
  const duration = (lastLeakTime.getTime() - firstLeakTime.getTime()) / (1000 * 60);

  // Find peak concentration
  const peakConcentration = Math.max(...leakReadings.map((r) => r.concentration));

  // Estimate leak rate
  const firstConc = leakReadings[0].concentration;
  const lastConc = leakReadings[leakReadings.length - 1].concentration;
  const estimatedRate = duration > 0 ? (lastConc - firstConc) / duration : 0;

  // Determine leak severity
  let severity: LeakSeverity;
  if (peakConcentration >= thresholds.critical) {
    severity = 'severe';
  } else if (peakConcentration >= thresholds.danger) {
    severity = 'major';
  } else if (peakConcentration >= thresholds.warning) {
    severity = 'moderate';
  } else {
    severity = 'minor';
  }

  // Estimate source type based on concentration pattern
  let estimatedSource: 'point' | 'diffuse' | 'unknown' = 'unknown';
  if (leakReadings.length >= 3) {
    const concentrations = leakReadings.map((r) => r.concentration);
    const variance = calculateVariance(concentrations);
    const mean = concentrations.reduce((a, b) => a + b, 0) / concentrations.length;
    const cv = Math.sqrt(variance) / mean; // Coefficient of variation

    if (cv < 0.2) {
      estimatedSource = 'diffuse'; // Steady, diffuse leak
    } else if (cv > 0.5) {
      estimatedSource = 'point'; // Variable, point source leak
    }
  }

  return {
    isLeaking,
    severity,
    duration,
    peakConcentration,
    estimatedRate,
    estimatedSource,
  };
}

/**
 * Calculate variance helper function
 */
function calculateVariance(values: number[]): number {
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const squaredDiffs = values.map((v) => Math.pow(v - mean, 2));
  return squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
}

/**
 * Calculate average gas concentration over period
 */
export function calculateAverageConcentration(
  readings: GasReading[],
  startDate: Date,
  endDate: Date,
): number {
  const filtered = readings.filter((r) => {
    const t = new Date(r.timestamp);
    return t >= startDate && t <= endDate;
  });

  if (filtered.length === 0) return 0;

  const sum = filtered.reduce((acc, r) => acc + r.concentration, 0);
  return sum / filtered.length;
}

/**
 * Calculate comprehensive gas statistics
 */
export function getGasStatistics(
  readings: GasReading[],
  startDate: Date,
  endDate: Date,
  gasType: GasType,
): GasStats {
  const filtered = readings.filter((r) => {
    const t = new Date(r.timestamp);
    return t >= startDate && t <= endDate && r.gasType === gasType;
  });

  if (filtered.length === 0) {
    return {
      avgConcentration: 0,
      maxConcentration: 0,
      minConcentration: 0,
      peakTime: null,
      safeHours: 0,
      warningHours: 0,
      dangerHours: 0,
      criticalHours: 0,
      leakDetectionCount: 0,
      avgLeakDuration: 0,
    };
  }

  const thresholds = GAS_THRESHOLDS[gasType];

  // Basic stats
  const concentrations = filtered.map((r) => r.concentration);
  const avgConcentration = concentrations.reduce((a, b) => a + b, 0) / concentrations.length;
  const maxConcentration = Math.max(...concentrations);
  const minConcentration = Math.min(...concentrations);

  // Find peak time
  const peakReading = filtered.find((r) => r.concentration === maxConcentration);
  const peakTime = peakReading ? new Date(peakReading.timestamp) : null;

  // Time in each toxicity level
  let safeHours = 0;
  let warningHours = 0;
  let dangerHours = 0;
  let criticalHours = 0;

  for (let i = 1; i < filtered.length; i++) {
    const conc = filtered[i].concentration;
    const hoursDiff =
      (new Date(filtered[i].timestamp).getTime() - new Date(filtered[i - 1].timestamp).getTime()) /
      (1000 * 60 * 60);

    if (conc <= thresholds.safe) {
      safeHours += hoursDiff;
    } else if (conc <= thresholds.warning) {
      warningHours += hoursDiff;
    } else if (conc <= thresholds.danger) {
      dangerHours += hoursDiff;
    } else {
      criticalHours += hoursDiff;
    }
  }

  // Leak detection
  let leakDetectionCount = 0;
  let totalLeakDuration = 0;
  let inLeak = false;
  let leakStart: Date | null = null;

  for (const reading of filtered) {
    const isAboveThreshold = reading.concentration > thresholds.warning;

    if (isAboveThreshold && !inLeak) {
      inLeak = true;
      leakStart = new Date(reading.timestamp);
      leakDetectionCount++;
    } else if (!isAboveThreshold && inLeak) {
      inLeak = false;
      if (leakStart) {
        const duration =
          (new Date(reading.timestamp).getTime() - leakStart.getTime()) / (1000 * 60);
        totalLeakDuration += duration;
      }
    }
  }

  const avgLeakDuration = leakDetectionCount > 0 ? totalLeakDuration / leakDetectionCount : 0;

  return {
    avgConcentration,
    maxConcentration,
    minConcentration,
    peakTime,
    safeHours,
    warningHours,
    dangerHours,
    criticalHours,
    leakDetectionCount,
    avgLeakDuration,
  };
}

/**
 * Generate context-aware recommendations
 */
export function generateGasRecommendations(
  gasType: GasType,
  concentration: number,
  toxicityLevel: ToxicityLevel,
  explosionRisk: ExplosionRiskInfo,
  leakInfo: LeakInfo,
  language: 'ar' | 'en',
): string[] {
  const recommendations: string[] = [];

  // Critical/Emergency recommendations
  if (explosionRisk.evacuationRequired) {
    recommendations.push(
      language === 'ar'
        ? '⚠️ إخلاء فوري مطلوب - خطر انفجار'
        : '⚠️ Immediate evacuation required - explosion hazard',
    );
  }

  if (toxicityLevel === 'critical' || toxicityLevel === 'lethal') {
    recommendations.push(
      language === 'ar'
        ? '🚨 مستويات خطيرة - اترك المنطقة فوراً'
        : '🚨 Critical levels - leave area immediately',
    );
  }

  // Leak-specific recommendations
  if (leakInfo.isLeaking) {
    if (leakInfo.severity === 'severe' || leakInfo.severity === 'major') {
      recommendations.push(
        language === 'ar'
          ? 'تسرب كبير مكتشف - أغلق مصدر الغاز'
          : 'Major leak detected - shut off gas source',
      );
    } else {
      recommendations.push(
        language === 'ar'
          ? 'تسرب مكتشف - افتح النوافذ للتهوية'
          : 'Leak detected - open windows for ventilation',
      );
    }
  }

  // Explosion risk recommendations
  if (explosionRisk.riskLevel === 'high' || explosionRisk.riskLevel === 'critical') {
    recommendations.push(
      language === 'ar' ? 'لا تشغل أي أجهزة كهربائية' : 'Do not operate any electrical devices',
    );
  }

  // Gas-specific recommendations
  if (gasType === 'CO' && toxicityLevel !== 'safe') {
    recommendations.push(
      language === 'ar'
        ? 'أول أكسيد الكربون مرتفع - تحقق من التهوية'
        : 'High CO levels - check ventilation systems',
    );
  }

  if (gasType === 'CH4' || gasType === 'LPG') {
    if (toxicityLevel === 'moderate' || toxicityLevel === 'high') {
      recommendations.push(
        language === 'ar' ? 'تحقق من أنابيب الغاز والوصلات' : 'Inspect gas pipes and connections',
      );
    }
  }

  if (gasType === 'H2S' && toxicityLevel !== 'safe') {
    recommendations.push(
      language === 'ar'
        ? 'كبريتيد الهيدروجين سام - ارتدِ معدات الحماية'
        : 'H2S is toxic - wear protective equipment',
    );
  }

  // Time-based warnings
  if (explosionRisk.timeToLEL !== null && explosionRisk.timeToLEL < 60) {
    recommendations.push(
      language === 'ar'
        ? `خطر الانفجار خلال ${explosionRisk.timeToLEL} دقيقة`
        : `Explosion risk in ${explosionRisk.timeToLEL} minutes`,
    );
  }

  // Safe level recommendations
  if (toxicityLevel === 'safe' && !leakInfo.isLeaking) {
    recommendations.push(
      language === 'ar'
        ? 'المستويات طبيعية - تابع المراقبة'
        : 'Levels normal - continue monitoring',
    );
  }

  return recommendations.slice(0, 3); // Max 3 recommendations
}

/**
 * Generate emergency actions
 */
export function generateEmergencyActions(
  toxicityLevel: ToxicityLevel,
  explosionRisk: ExplosionRiskInfo,
  leakInfo: LeakInfo,
  language: 'ar' | 'en',
): string[] {
  const actions: string[] = [];

  if (explosionRisk.isExplosive || explosionRisk.evacuationRequired) {
    actions.push(language === 'ar' ? '1. إخلاء المبنى فوراً' : '1. Evacuate building immediately');
    actions.push(language === 'ar' ? '2. اتصل بالطوارئ (998)' : '2. Call emergency services (998)');
    actions.push(
      language === 'ar'
        ? '3. لا تستخدم الهواتف أو الكهرباء'
        : '3. Do not use phones or electricity',
    );
  } else if (toxicityLevel === 'critical' || toxicityLevel === 'lethal') {
    actions.push(language === 'ar' ? '1. غادر المنطقة فوراً' : '1. Leave area immediately');
    actions.push(language === 'ar' ? '2. اطلب المساعدة الطبية' : '2. Seek medical attention');
    actions.push(language === 'ar' ? '3. افتح جميع النوافذ' : '3. Open all windows');
  } else if (leakInfo.severity === 'major' || leakInfo.severity === 'severe') {
    actions.push(language === 'ar' ? '1. أغلق صمام الغاز الرئيسي' : '1. Close main gas valve');
    actions.push(language === 'ar' ? '2. افتح النوافذ والأبواب' : '2. Open windows and doors');
    actions.push(language === 'ar' ? '3. اتصل بالصيانة' : '3. Contact maintenance');
  }

  return actions;
}

/**
 * Get comprehensive gas status
 */
export function getGasStatus(
  readings: GasReading[],
  currentConcentration: number,
  gasType: GasType,
): GasStatus {
  const now = new Date();

  // Calculate time periods
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 24 * 60 * 60 * 1000);
  const startOfWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  // Get statistics
  const todayStats = getGasStatistics(readings, startOfToday, now, gasType);
  const yesterdayStats = getGasStatistics(readings, startOfYesterday, startOfToday, gasType);
  const weekStats = getGasStatistics(readings, startOfWeek, now, gasType);
  const monthStats = getGasStatistics(readings, startOfMonth, now, gasType);

  // Get toxicity level
  const toxicityLevel = getToxicityLevel(gasType, currentConcentration);

  // Get explosion risk
  const explosionRisk = getExplosionRisk(gasType, currentConcentration, readings);

  // Detect leak
  const leakInfo = detectLeak(readings, currentConcentration, gasType);

  // Generate recommendations
  const recommendations = generateGasRecommendations(
    gasType,
    currentConcentration,
    toxicityLevel,
    explosionRisk,
    leakInfo,
    'en',
  );

  // Generate emergency actions
  const emergencyActions = generateEmergencyActions(toxicityLevel, explosionRisk, leakInfo, 'en');

  return {
    gasType,
    currentConcentration,
    toxicityLevel,
    explosionRisk,
    leakInfo,
    todayStats,
    yesterdayStats,
    weekStats,
    monthStats,
    recommendations,
    emergencyActions,
  };
}

// ==================== Formatting Functions ====================

/**
 * Format gas concentration with unit
 */
export function formatConcentration(concentration: number, language: 'ar' | 'en'): string {
  if (language === 'ar') {
    const arabicNum = concentration.toFixed(0).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    return `${arabicNum} جزء/مليون`;
  }
  return `${concentration.toFixed(0)} ppm`;
}

/**
 * Format gas type name
 */
export function formatGasType(gasType: GasType, language: 'ar' | 'en'): string {
  const names: Record<GasType, { ar: string; en: string }> = {
    CH4: { ar: 'ميثان (غاز طبيعي)', en: 'Methane (Natural Gas)' },
    CO: { ar: 'أول أكسيد الكربون', en: 'Carbon Monoxide' },
    LPG: { ar: 'غاز البترول المسال', en: 'LPG (Propane/Butane)' },
    H2S: { ar: 'كبريتيد الهيدروجين', en: 'Hydrogen Sulfide' },
    NH3: { ar: 'أمونيا', en: 'Ammonia' },
    CO2: { ar: 'ثاني أكسيد الكربون', en: 'Carbon Dioxide' },
    O2: { ar: 'أكسجين', en: 'Oxygen' },
    VOC: { ar: 'مركبات عضوية متطايرة', en: 'Volatile Organic Compounds' },
    unknown: { ar: 'غاز غير معروف', en: 'Unknown Gas' },
  };
  return names[gasType][language];
}

/**
 * Format toxicity level
 */
export function formatToxicityLevel(level: ToxicityLevel, language: 'ar' | 'en'): string {
  const labels: Record<ToxicityLevel, { ar: string; en: string }> = {
    safe: { ar: 'آمن', en: 'Safe' },
    low: { ar: 'منخفض', en: 'Low' },
    moderate: { ar: 'متوسط', en: 'Moderate' },
    high: { ar: 'مرتفع', en: 'High' },
    critical: { ar: 'حرج', en: 'Critical' },
    lethal: { ar: 'قاتل', en: 'Lethal' },
  };
  return labels[level][language];
}

/**
 * Format explosion risk
 */
export function formatExplosionRisk(risk: ExplosionRisk, language: 'ar' | 'en'): string {
  const labels: Record<ExplosionRisk, { ar: string; en: string }> = {
    none: { ar: 'لا يوجد', en: 'None' },
    low: { ar: 'منخفض', en: 'Low' },
    moderate: { ar: 'متوسط', en: 'Moderate' },
    high: { ar: 'مرتفع', en: 'High' },
    critical: { ar: 'حرج', en: 'Critical' },
  };
  return labels[risk][language];
}

/**
 * Format leak severity
 */
export function formatLeakSeverity(severity: LeakSeverity, language: 'ar' | 'en'): string {
  const labels: Record<LeakSeverity, { ar: string; en: string }> = {
    none: { ar: 'لا يوجد', en: 'None' },
    minor: { ar: 'بسيط', en: 'Minor' },
    moderate: { ar: 'متوسط', en: 'Moderate' },
    major: { ar: 'كبير', en: 'Major' },
    severe: { ar: 'شديد', en: 'Severe' },
  };
  return labels[severity][language];
}

/**
 * Format duration
 */
export function formatDuration(minutes: number, language: 'ar' | 'en'): string {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);

  if (language === 'ar') {
    if (hours > 0) {
      return `${hours.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])} س ${mins
        .toString()
        .replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])} د`;
    }
    return `${mins.toString().replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)])} د`;
  }

  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

/**
 * Format LEL percentage
 */
export function formatLELPercentage(percentage: number, language: 'ar' | 'en'): string {
  if (language === 'ar') {
    const arabicNum = percentage.toFixed(1).replace(/\d/g, (d) => '٠١٢٣٤٥٦٧٨٩'[parseInt(d)]);
    return `${arabicNum}٪ من حد الانفجار`;
  }
  return `${percentage.toFixed(1)}% of LEL`;
}

// ==================== Color Functions ====================

/**
 * Get color for toxicity level
 */
export function getToxicityLevelColor(level: ToxicityLevel): string {
  const colors: Record<ToxicityLevel, string> = {
    safe: '#10b981', // Green
    low: '#84cc16', // Lime
    moderate: '#f59e0b', // Orange
    high: '#ef4444', // Red
    critical: '#dc2626', // Dark red
    lethal: '#7f1d1d', // Very dark red
  };
  return colors[level];
}

/**
 * Get color for explosion risk
 */
export function getExplosionRiskColor(risk: ExplosionRisk): string {
  const colors: Record<ExplosionRisk, string> = {
    none: '#10b981', // Green
    low: '#84cc16', // Lime
    moderate: '#f59e0b', // Orange
    high: '#ef4444', // Red
    critical: '#7f1d1d', // Very dark red
  };
  return colors[risk];
}

/**
 * Get color for leak severity
 */
export function getLeakSeverityColor(severity: LeakSeverity): string {
  const colors: Record<LeakSeverity, string> = {
    none: '#10b981', // Green
    minor: '#84cc16', // Lime
    moderate: '#f59e0b', // Orange
    major: '#ef4444', // Red
    severe: '#7f1d1d', // Very dark red
  };
  return colors[severity];
}

/**
 * Get color for gas concentration (based on toxicity)
 */
export function getConcentrationColor(gasType: GasType, concentration: number): string {
  const level = getToxicityLevel(gasType, concentration);
  return getToxicityLevelColor(level);
}
